<template>
  <labeled-section
    title="Additional Items"
    icon="fal fa-plus"
    v-if="purchase?.availableAddons?.length"
  >
    <v-card variant="outlined" class="purchase-addon-selector" v-if="!isMobile">
      <v-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Item</th>
              <th class="purchase-addon-description-header">Description</th>
              <th class="text-center">Selection</th>
              <th class="text-right" width="90px">Total</th>
            </tr>
          </thead>
          <tbody
            v-for="addonConfig in purchase.availableAddons"
            :key="addonConfig.configurationId!"
          >
            <purchase-addon-selector-row
              :purchase="purchase"
              :addonConfig="addonConfig"
            >
            </purchase-addon-selector-row>
            <tr class="purchase-addon-row2">
              <td
                colspan="3"
                class="purchase-addon-description"
                v-text="addonConfig.description"
              ></td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </v-card>
    <div v-else>
      <v-card
        v-for="addonConfig in purchase.availableAddons"
        :key="addonConfig.configurationId!"
        class="quantity-card addon-card mb-3"
      >
        <purchase-addon-selector-row
          :purchase="purchase"
          :addonConfig="addonConfig"
          :isMobile="isMobile"
        />
      </v-card>
    </div>
  </labeled-section>
</template>

<style lang="scss">
.purchase-addon-selector {
  .purchase-addon-description {
    // Prevent the description from trying to encroach too much on the title.
    // 400px prevents "Purchase Protection" from wrapping on a full width screen.
    max-width: 400px;
    white-space: pre-wrap;
  }

  .title {
    line-height: 1.1;
  }

  table {
    td {
      vertical-align: top;
      padding-top: 12px !important;
      padding-bottom: 12px !important;

      > .v-btn {
        margin-top: 4px;
        width: 140px;
      }

      &:last-child {
        // Price column - make the number vert align with the input column
        padding-top: 24px !important;
      }
    }
  }

  // On small screens (XS breakpoint),
  // display the description in a second row underneath the name/selector/subtotal row.
  .purchase-addon-row2 {
    // Hide the second description row for small screens by default
    display: none;

    .purchase-addon-description {
      // Reduce padding to bring the description up against its main row above it.
      padding-top: 0 !important;
    }
  }

  @media screen and (max-width: 599px) {
    td > .v-btn {
      width: 80px !important;
    }
    .purchase-addon-description-header {
      display: none;
    }
    .purchase-addon-row2 {
      display: table-row;
    }

    // Make hovering one row hover both of them:
    .purchase-addon-selector-row:hover + .purchase-addon-row2
      // pending https://developer.mozilla.org/en-US/docs/Web/CSS/:has support:
      //, .purchase-addon-selector-row:has(+ .purchase-addon-row2:hover)
    {
      background: #eeeeee;
    }

    .purchase-addon-selector-row {
      td {
        // Hide the border on the input rows below the small breakpoint,
        // since the second row with the description will be contributing the border below that item.
        border: none !important;

        &.purchase-addon-description {
          // Hide the main description entirely
          display: none;
        }
      }
    }
  }
}
</style>

<script setup lang="ts">
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import { useDisplay } from "vuetify";
import PurchaseAddonSelectorRow from "./purchase-addon-selector-row.vue";
const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);

defineProps<{
  purchase: TicketPurchaseDtoViewModel;
}>();
</script>
